import { useState, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';

/**
 * Custom hook for First Freight API place lookup
 * Provides postal code lookup and place search functionality
 */
export const useFirstFreightPlaces = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Look up place details by postal code
   * @param {string} postcode - South African postal code
   * @returns {Promise<{success: boolean, places: Array}>}
   */
  const lookupByPostcode = useCallback(async (postcode) => {
    if (!postcode || postcode.length < 4) {
      return { success: false, places: [] };
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(`${API}/ff/places/${postcode}`);
      return response.data;
    } catch (err) {
      console.error('Place lookup error:', err);
      setError(err.message);
      return { success: false, places: [], error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Search places by name
   * @param {string} name - Place/town name to search
   * @returns {Promise<{success: boolean, places: Array}>}
   */
  const searchByName = useCallback(async (name) => {
    if (!name || name.length < 2) {
      return { success: false, places: [] };
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(`${API}/ff/places/search/${encodeURIComponent(name)}`);
      return response.data;
    } catch (err) {
      console.error('Place search error:', err);
      setError(err.message);
      return { success: false, places: [], error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Test First Freight API connection
   * @returns {Promise<{success: boolean, message: string}>}
   */
  const testConnection = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/ff/test-connection`);
      return response.data;
    } catch (err) {
      return { success: false, message: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    error,
    lookupByPostcode,
    searchByName,
    testConnection,
  };
};

export default useFirstFreightPlaces;
